import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, CalendarClock, Check, CircleDashed, FileText, Lock, Plus, Send, Trash2, Upload, X } from "lucide-react";
import { api, ApiError } from "../lib/api";

/**
 * Werkzeuge für den Betrieb: Termine vorschlagen, Angebot schreiben,
 * Arbeitsbericht erstellen. Alles landet beim Kunden auf seiner
 * Verfolgungsseite und wird ihm per E-Mail angekündigt.
 */

const EUR = (cent: number) =>
  (cent / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/** "12,50" oder "12.50" -> 1250 Cent. */
function centAus(text: string): number {
  const n = Number(String(text).replace(",", ".").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** Vorschlag für das Datumsfeld: morgen, zur vollen Stunde. */
function startwert(tagePlus: number, stunde: number): string {
  const d = new Date();
  d.setDate(d.getDate() + tagePlus);
  d.setHours(stunde, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Dokument als PDF in einem neuen Tab öffnen.
 * Der Abruf ist angemeldet, ein normaler Link trägt die Anmeldung nicht mit -
 * deshalb erst holen, dann als Datei im Browser öffnen.
 */
async function pdfOeffnen(anfrageId: string, dokId: string): Promise<void> {
  try {
    const blob = await api<Blob>(`/admin/anfragen/${anfrageId}/dokumente/${dokId}/pdf`);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    // Erst freigeben, wenn der neue Tab die Datei geladen hat.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    alert("Das PDF konnte nicht erstellt werden. Bitte noch einmal versuchen.");
  }
}

/** Jetzt, im Format des Datumsfeldes - als frühestmöglicher Termin. */
function jetztFuerFeld(): string {
  return startwert(0, new Date().getHours());
}

/** ISO-Zeitpunkt vom Server in das Format des Datumsfeldes (Ortszeit). */
function fuerFeld(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ===========================================================================
// Termine vorschlagen
// ===========================================================================
export function TermineVorschlagen({
  anfrageId, onFertig, assignedUserId,
}: { anfrageId: string; onFertig: () => void; assignedUserId?: string | null }) {
  const [art, setArt] = useState<"besichtigung" | "ausfuehrung">("besichtigung");
  const [dauer, setDauer] = useState(60);
  const [zeiten, setZeiten] = useState<string[]>([
    startwert(1, 9), startwert(1, 14), startwert(2, 10),
  ]);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  // Für wen wird gesucht? Leer = alle Termine des Betriebs zählen als belegt.
  const [fuerWen, setFuerWen] = useState<string>(assignedUserId ?? "");
  const [sucheLaeuft, setSucheLaeuft] = useState(false);
  const { data: team } = useQuery({
    queryKey: ["team"],
    queryFn: () => api<{ items: { id: string; name: string; role: string }[] }>("/admin/users"),
  });

  function aendern(i: number, wert: string) {
    setZeiten((z) => z.map((x, k) => (k === i ? wert : x)));
  }

  /**
   * Freie Zeiten holen und die Felder damit füllen.
   * Der Server rechnet mit den Arbeitszeiten des Betriebs und lässt alles
   * aus, was schon vergeben ist (mit Fahrzeit-Puffer dazwischen).
   */
  async function freieZeitenHolen() {
    setSucheLaeuft(true); setMeldung(null);
    try {
      const p = new URLSearchParams({ dauer: String(dauer), anzahl: String(zeiten.length || 3) });
      if (fuerWen) p.set("userId", fuerWen);
      const r = await api<{ vorschlaege: string[]; belegt: number }>(`/admin/termine/frei?${p}`);
      if (!r.vorschlaege.length) {
        setMeldung("In den nächsten Wochen ist nichts frei. Bitte Arbeitszeiten in den Einstellungen prüfen oder von Hand eintragen.");
        return;
      }
      setZeiten(r.vorschlaege.map(fuerFeld));
      const wer = fuerWen ? (team?.items.find((t) => t.id === fuerWen)?.name ?? "dem Mitarbeiter") : "dem Betrieb";
      setMeldung(`${r.vorschlaege.length} freie Zeiten eingetragen — ${r.belegt} bestehende Termine bei ${wer} wurden ausgelassen. Sie können sie noch ändern.`);
    } catch (e) {
      setMeldung(e instanceof ApiError ? e.message : "Die freien Zeiten konnten nicht geladen werden.");
    } finally {
      setSucheLaeuft(false);
    }
  }

  async function senden() {
    const gueltig = zeiten.filter(Boolean);
    if (!gueltig.length) return;
    setLaeuft(true); setMeldung(null);
    try {
      await api(`/admin/anfragen/${anfrageId}/termine`, {
        method: "POST",
        body: {
          art, dauerMinuten: dauer,
          zeitpunkte: gueltig.map((z) => new Date(z).toISOString()),
        },
      });
      setMeldung(`${gueltig.length} Termine verschickt — der Kunde wählt jetzt aus.`);
      onFertig();
    } catch {
      setMeldung("Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally { setLaeuft(false); }
  }

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="input input-sm w-auto" value={art}
          onChange={(e) => setArt(e.target.value as typeof art)}>
          <option value="besichtigung">Besichtigung</option>
          <option value="ausfuehrung">Ausführung</option>
        </select>
        <select className="input input-sm w-auto" value={dauer}
          onChange={(e) => setDauer(Number(e.target.value))}>
          {[30, 45, 60, 90, 120, 240, 480].map((m) => (
            <option key={m} value={m}>{m < 60 ? `${m} Min.` : `${m / 60} Std.`}</option>
          ))}
        </select>
      </div>

      {/* Freie Zeiten aus dem eigenen Kalender: der AuftragsManager kennt jeden
          bestätigten Termin und schlägt daraus passende Lücken vor. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2">
        <select className="input input-sm w-auto" value={fuerWen}
          onChange={(e) => setFuerWen(e.target.value)}>
          <option value="">Ganzer Betrieb</option>
          {(team?.items ?? []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <button type="button" className="btn-ghost btn-sm" disabled={sucheLaeuft}
          onClick={freieZeitenHolen}>
          {sucheLaeuft ? "Suche …" : "Freie Zeiten vorschlagen"}
        </button>
        <span className="text-xs text-slate-500">
          füllt die Felder unten mit Zeiten, die noch frei sind
        </span>
      </div>

      <div className="space-y-2">
        {zeiten.map((z, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="datetime-local"
              className="input input-sm flex-1"
              /* Ein Termin in der Vergangenheit ist immer ein Vertipper
                 (falsches Jahr, falscher Monat) - der Server weist ihn ab,
                 der Browser warnt schon beim Tippen. */
              min={jetztFuerFeld()}
              value={z}
              onChange={(e) => aendern(i, e.target.value)}
            />
            {zeiten.length > 1 && (
              <button type="button" title="Entfernen"
                onClick={() => setZeiten((v) => v.filter((_, k) => k !== i))}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {zeiten.length < 6 && (
          <button type="button" className="btn-secondary btn-sm"
            onClick={() => setZeiten((v) => [...v, startwert(v.length + 1, 10)])}>
            <Plus className="h-3.5 w-3.5" /> Termin
          </button>
        )}
        <button type="button" className="btn-primary btn-sm" disabled={laeuft} onClick={senden}>
          <Send className="h-3.5 w-3.5" />
          {laeuft ? "Wird verschickt …" : "Dem Kunden vorschlagen"}
        </button>
      </div>

      {meldung && <p className="mt-2 text-sm text-slate-600">{meldung}</p>}
      <p className="mt-2 text-xs text-slate-400">
        Der Kunde bekommt eine E-Mail und wählt einen Termin auf seiner
        Verfolgungsseite. Die Auswahl steht danach hier oben.
      </p>
    </div>
  );
}

// ===========================================================================
// Angebot / Arbeitsbericht
// ===========================================================================
type Zeile = { bezeichnung: string; menge: string; einheit: string; preis: string };

const ART_NAME = { angebot: "Angebot", arbeitsbericht: "Arbeitsbericht", rechnung: "Rechnung" } as const;

interface KatalogPosition {
  id: string; bezeichnung: string; einheit: string; einzelpreis_cent: number;
}

/** Vorbefüllung nach einem Storno: das Original, bereit zum Korrigieren. */
export interface RechnungsVorlage {
  titel: string;
  positionen: { bezeichnung: string; menge: number; einheit: string; einzelpreisCent: number }[];
  mwstProzent: number;
  kundeAdresse: string | null;
  textOben: string | null;
  textUnten: string | null;
}

export function DokumentSchreiben({
  anfrageId, art, onFertig, vorlage, kundeAnschrift,
}: { anfrageId: string; art: "angebot" | "arbeitsbericht" | "rechnung"; onFertig: () => void; vorlage?: RechnungsVorlage; kundeAnschrift?: string }) {
  const istAngebot = art === "angebot";
  // Rechnung rechnet wie das Angebot mit Preisen; nur der Bericht nicht.
  const mitPreisen = art !== "arbeitsbericht";
  const qc = useQueryClient();
  // Gespeicherte Positionen des Betriebs - einmal getippt, immer wieder nutzbar.
  const { data: katalog } = useQuery({
    queryKey: ["katalog"],
    queryFn: () => api<{ positionen: KatalogPosition[] }>("/admin/katalog"),
    enabled: mitPreisen,
  });
  const [titel, setTitel] = useState(vorlage?.titel ?? (istAngebot ? "" : ART_NAME[art]));
  const [textOben, setTextOben] = useState(
    vorlage?.textOben
    ?? (istAngebot
      ? "vielen Dank für Ihr Vertrauen. Wir schlagen folgende Arbeiten vor:"
      : art === "rechnung"
        ? "vielen Dank für Ihren Auftrag. Wir berechnen folgende Leistungen:"
        : "die Arbeiten sind abgeschlossen. Ausgeführt wurde:"),
  );
  const [textUnten, setTextUnten] = useState(vorlage?.textUnten ?? "");
  const [mwst, setMwst] = useState(vorlage?.mwstProzent ?? 19);
  const [zeilen, setZeilen] = useState<Zeile[]>(
    vorlage?.positionen.length
      ? vorlage.positionen.map((p) => ({
          bezeichnung: p.bezeichnung,
          menge: String(p.menge).replace(".", ","),
          einheit: p.einheit,
          preis: (p.einzelpreisCent / 100).toFixed(2).replace(".", ","),
        }))
      : [{ bezeichnung: "", menge: "1", einheit: mitPreisen ? "Stk" : "erledigt", preis: "" }],
  );
  // Anschrift des Kunden - Pflicht auf der Rechnung (§14 UStG, E-Rechnung).
  // Nach einem Storno gilt die Anschrift der Originalrechnung, sonst die aus
  // der Anfrage - abtippen muss sie niemand mehr.
  const [kundeAdresse, setKundeAdresse] = useState(vorlage?.kundeAdresse ?? kundeAnschrift ?? "");
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const netto = zeilen.reduce(
    (s, z) => s + Math.round(Number(z.menge.replace(",", ".") || 0) * centAus(z.preis)), 0);
  const brutto = Math.round(netto * (1 + mwst / 100));

  function aendern(i: number, feld: keyof Zeile, wert: string) {
    setZeilen((v) => v.map((z, k) => (k === i ? { ...z, [feld]: wert } : z)));
  }

  async function senden() {
    if (!titel.trim()) { setMeldung("Bitte einen Titel angeben."); return; }
    const positionen = zeilen
      .filter((z) => z.bezeichnung.trim())
      .map((z) => ({
        bezeichnung: z.bezeichnung.trim(),
        menge: Number(z.menge.replace(",", ".")) || 1,
        einheit: z.einheit || "Stk",
        einzelpreisCent: centAus(z.preis),
      }));
    if (!positionen.length) { setMeldung("Bitte mindestens eine Zeile ausfüllen."); return; }
    if (art === "rechnung" && !/\d{5}\s+\S/.test(kundeAdresse)) {
      setMeldung("Bitte die Anschrift des Kunden angeben: Straße Nr, PLZ Ort.");
      return;
    }

    setLaeuft(true); setMeldung(null);
    try {
      await api(`/admin/anfragen/${anfrageId}/dokumente`, {
        method: "POST",
        body: {
          art, titel: titel.trim(), positionen, mwstProzent: mwst,
          textOben: textOben.trim() || undefined,
          textUnten: textUnten.trim() || undefined,
          gueltigBis: istAngebot
            ? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
            : undefined,
          kundeAdresse: art === "rechnung" ? kundeAdresse.trim() : undefined,
        },
      });
      setMeldung(istAngebot
        ? "Angebot verschickt — der Kunde kann es online annehmen."
        : art === "rechnung"
          ? "Rechnung verschickt — samt E-Rechnung (XML) auf der Kundenseite."
          : "Arbeitsbericht verschickt.");
      onFertig();
    } catch {
      setMeldung("Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally { setLaeuft(false); }
  }

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <input
        className="input input-sm mb-2"
        placeholder={istAngebot ? "Titel, z. B. Badsanierung Gäste-WC" : art === "rechnung" ? "Titel, z. B. Badsanierung Gäste-WC" : "Titel des Berichts"}
        value={titel}
        onChange={(e) => setTitel(e.target.value)}
      />
      {art === "rechnung" && (
        <div className="mb-2">
          <input
            className="input input-sm"
            placeholder="Anschrift des Kunden: Straße Nr, PLZ Ort"
            value={kundeAdresse}
            onChange={(e) => setKundeAdresse(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-400">
            Pflicht auf der Rechnung — steht so auch in der E-Rechnung (XML).
          </p>
        </div>
      )}
      <textarea
        className="input input-sm mb-3"
        rows={2}
        placeholder="Einleitender Satz"
        value={textOben}
        onChange={(e) => setTextOben(e.target.value)}
      />

      <div className="space-y-2">
        {zeilen.map((z, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="input input-sm min-w-[140px] flex-1"
              placeholder="Leistung"
              value={z.bezeichnung}
              onChange={(e) => aendern(i, "bezeichnung", e.target.value)}
            />
            <input
              className="input input-sm w-16"
              placeholder="Menge"
              value={z.menge}
              onChange={(e) => aendern(i, "menge", e.target.value)}
            />
            <input
              className="input input-sm w-20"
              placeholder="Einheit"
              value={z.einheit}
              onChange={(e) => aendern(i, "einheit", e.target.value)}
            />
            {mitPreisen && (
              <input
                className="input input-sm w-24"
                placeholder="€ / Einheit"
                value={z.preis}
                onChange={(e) => aendern(i, "preis", e.target.value)}
              />
            )}
            {mitPreisen && (
              <button type="button" title="Position im Katalog speichern - beim nächsten Mal per Klick einfügen"
                onClick={async () => {
                  if (!z.bezeichnung.trim()) return;
                  await api("/admin/katalog", {
                    method: "POST",
                    body: {
                      bezeichnung: z.bezeichnung.trim(),
                      einheit: z.einheit || "Stk",
                      einzelpreisCent: centAus(z.preis),
                    },
                  });
                  qc.invalidateQueries({ queryKey: ["katalog"] });
                  setMeldung(`"${z.bezeichnung.trim()}" im Katalog gespeichert.`);
                }}
                className="rounded-lg p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-600">
                <Bookmark className="h-4 w-4" />
              </button>
            )}
            {zeilen.length > 1 && (
              <button type="button" title="Zeile entfernen"
                onClick={() => setZeilen((v) => v.filter((_, k) => k !== i))}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary btn-sm"
          onClick={() => setZeilen((v) => [...v,
            { bezeichnung: "", menge: "1", einheit: mitPreisen ? "Stk" : "erledigt", preis: "" }])}>
          <Plus className="h-3.5 w-3.5" /> Zeile
        </button>
        {mitPreisen && (katalog?.positionen.length ?? 0) > 0 && (
          <select
            className="input input-sm w-auto"
            value=""
            onChange={(e) => {
              const k = katalog?.positionen.find((x) => x.id === e.target.value);
              if (!k) return;
              setZeilen((v) => {
                // Leere erste Zeile ersetzen statt eine zweite anzuhängen.
                const neu = {
                  bezeichnung: k.bezeichnung, menge: "1", einheit: k.einheit,
                  preis: (k.einzelpreis_cent / 100).toFixed(2).replace(".", ","),
                };
                if (v.length === 1 && !v[0]?.bezeichnung.trim()) return [neu];
                return [...v, neu];
              });
            }}
          >
            <option value="">Aus Katalog einfügen …</option>
            {(katalog?.positionen ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.bezeichnung} ({EUR(k.einzelpreis_cent)}/{k.einheit})
              </option>
            ))}
          </select>
        )}
      </div>

      {mitPreisen && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <label className="flex items-center gap-2 text-slate-500">
            MwSt.
            <select className="input input-sm w-auto py-1" value={mwst}
              onChange={(e) => setMwst(Number(e.target.value))}>
              <option value={19}>19 %</option>
              <option value={7}>7 %</option>
              <option value={0}>0 %</option>
            </select>
          </label>
          <span className="text-slate-500">
            Netto {EUR(netto)} · <span className="font-semibold text-slate-900">Gesamt {EUR(brutto)}</span>
          </span>
        </div>
      )}

      <textarea
        className="input input-sm mt-3"
        rows={2}
        placeholder="Schlusssatz, z. B. Hinweise zur Ausführung"
        value={textUnten}
        onChange={(e) => setTextUnten(e.target.value)}
      />

      <button type="button" className="btn-primary btn-sm mt-3" disabled={laeuft} onClick={senden}>
        {art === "arbeitsbericht" ? <Check className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
        {laeuft ? "Wird verschickt …" : `${ART_NAME[art]} verschicken`}
      </button>
      {meldung && <p className="mt-2 text-sm text-slate-600">{meldung}</p>}
    </div>
  );
}

interface TerminZeile {
  id: string; art: string; beginnt_am: string; dauer_minuten: number;
  gewaehlt: boolean; gewaehlt_am: string | null;
}
interface DokumentZeile {
  id: string; art: "angebot" | "arbeitsbericht" | "rechnung"; nummer: string; titel: string;
  brutto_cent: number; angenommen_am: string | null; abgelehnt_am: string | null;
  storniert_am: string | null; storno_zu: string | null;
  created_at: string;
}

const wann = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", {
    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });

/**
 * Was bereits beim Kunden liegt: vorgeschlagene Termine (mit seiner Auswahl)
 * und verschickte Angebote/Berichte samt Antwort. Ohne diese Übersicht war
 * das Werkzeug "write-only" - man konnte senden, aber nie sehen, was daraus
 * geworden ist.
 */
function KundenUebersicht({
  anfrageId, onStorno,
}: { anfrageId: string; onStorno: (vorlage: RechnungsVorlage) => void }) {
  const qc = useQueryClient();
  const [stornoLaeuft, setStornoLaeuft] = useState(false);
  const { data: t } = useQuery({
    queryKey: ["termine", anfrageId],
    queryFn: () => api<{ termine: TerminZeile[] }>(`/admin/anfragen/${anfrageId}/termine`),
  });
  const { data: d } = useQuery({
    queryKey: ["dokumente", anfrageId],
    queryFn: () => api<{ dokumente: DokumentZeile[] }>(`/admin/anfragen/${anfrageId}/dokumente`),
  });

  async function stornieren(dok: DokumentZeile) {
    const sicher = window.confirm(
      `Rechnung ${dok.nummer} stornieren?\n\nDer Kunde bekommt eine Stornorechnung und eine E-Mail. `
      + "Danach öffnet sich das Formular mit den alten Positionen für die neue Rechnung.",
    );
    if (!sicher) return;
    setStornoLaeuft(true);
    try {
      const antwort = await api<{ vorlage: RechnungsVorlage }>(
        `/admin/anfragen/${anfrageId}/dokumente/${dok.id}/storno`, { method: "POST" });
      qc.invalidateQueries({ queryKey: ["dokumente", anfrageId] });
      qc.invalidateQueries({ queryKey: ["anfrage", anfrageId] });
      onStorno(antwort.vorlage);
    } catch {
      window.alert("Das Stornieren hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally { setStornoLaeuft(false); }
  }

  const termine = t?.termine ?? [];
  const dokumente = d?.dokumente ?? [];
  if (!termine.length && !dokumente.length) return null;

  const gewaehlt = termine.find((x) => x.gewaehlt);

  return (
    <div className="mb-4 space-y-2 rounded-xl bg-slate-50 p-3">
      {termine.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4 flex-none text-slate-400" />
          {gewaehlt ? (
            <span>
              Kunde hat gewählt:{" "}
              <b className="text-emerald-700">{wann(gewaehlt.beginnt_am)} Uhr</b>
              <span className="text-slate-400"> · {gewaehlt.art === "besichtigung" ? "Besichtigung" : "Ausführung"}</span>
            </span>
          ) : (
            <span className="text-slate-600">
              {termine.length} Termin{termine.length > 1 ? "e" : ""} vorgeschlagen — Kunde hat noch nicht gewählt
            </span>
          )}
        </div>
      )}
      {dokumente.map((dok) => (
        <div key={dok.id} className="flex flex-wrap items-center gap-2 text-sm">
          <FileText className="h-4 w-4 flex-none text-slate-400" />
          <span className={dok.storniert_am ? "text-slate-400 line-through" : "text-slate-600"}>
            {dok.storno_zu ? "Stornorechnung" : ART_NAME[dok.art] ?? "Dokument"} {dok.nummer}
            {dok.art !== "arbeitsbericht" && dok.brutto_cent !== 0 && (
              <span className="text-slate-400"> · {EUR(dok.brutto_cent)}</span>
            )}
          </span>
          {dok.storniert_am && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
              storniert
            </span>
          )}
          {/* Zum Ausdrucken, Abheften oder per Mail weiterschicken - dasselbe
              Blatt, das der Kunde auf seiner Seite sieht. Der Abruf braucht
              die Anmeldung, deshalb kein einfacher Link. */}
          <button
            type="button"
            onClick={() => pdfOeffnen(anfrageId, dok.id)}
            className="rounded-lg border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-500 hover:border-slate-300 hover:text-slate-800"
          >
            PDF
          </button>
          {dok.art === "rechnung" && !dok.storniert_am && !dok.storno_zu && (
            <button type="button" disabled={stornoLaeuft}
              onClick={() => stornieren(dok)}
              title="Rechnung stornieren und neu ausstellen"
              className="rounded-lg border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-500 hover:border-rose-200 hover:text-rose-600">
              Stornieren & neu ausstellen
            </button>
          )}
          {dok.art === "angebot" && (dok.angenommen_am ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              <Check className="h-3 w-3" /> angenommen
            </span>
          ) : dok.abgelehnt_am ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
              <X className="h-3 w-3" /> abgelehnt
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
              <CircleDashed className="h-3 w-3" /> offen
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Fertige PDF hochladen statt selbst erstellen - z.B. die Rechnung aus
 * Lexware oder das Angebot aus dem gewohnten Programm. Landet beim Kunden
 * unter "Ihre Unterlagen"; ein Rechnungs-Upload schaltet den Vorgang
 * automatisch auf "Rechnung gestellt".
 */
function PdfHochladen({
  anfrageId, art, onFertig,
}: { anfrageId: string; art: "angebot" | "rechnung"; onFertig: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function hochladen(datei: File | undefined) {
    if (!datei) return;
    setLaeuft(true); setMeldung(null);
    try {
      const form = new FormData();
      form.append("docType", art);
      form.append("file", datei);
      await api(`/admin/anfragen/${anfrageId}/documents`, { method: "POST", formData: form });
      setMeldung(`${ART_NAME[art]} hochgeladen — der Kunde findet die Datei auf seiner Seite.`);
      onFertig();
    } catch {
      setMeldung("Hochladen fehlgeschlagen. Bitte eine PDF-Datei wählen und noch einmal versuchen.");
    } finally { setLaeuft(false); }
  }

  return (
    <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
      <Upload className="mx-auto mb-2 h-6 w-6 text-slate-400" />
      <p className="mb-1 text-sm font-medium text-slate-700">
        Fertige {ART_NAME[art]}-PDF hochladen
      </p>
      <p className="mb-4 text-xs text-slate-500">
        Zum Beispiel aus Lexware, sevDesk oder Word als PDF gespeichert.
        Der Kunde sieht die Datei sofort auf seiner Auftragsseite.
      </p>
      <label className="btn-primary btn-sm inline-flex cursor-pointer">
        <Upload className="h-3.5 w-3.5" />
        {laeuft ? "Wird hochgeladen …" : "PDF auswählen"}
        <input type="file" accept="application/pdf" className="hidden" disabled={laeuft}
          onChange={(e) => hochladen(e.target.files?.[0])} />
      </label>
      {meldung && <p className="mt-3 text-sm text-slate-600">{meldung}</p>}
    </div>
  );
}

/** Welcher Reiter ist je nach Stand des Vorgangs der nächste Schritt? */
function passenderTab(status?: string): "termine" | "angebot" | "bericht" | "rechnung" {
  if (status === "inspection_done" || status === "quote_sent") return "angebot";
  if (status === "in_progress") return "bericht";
  if (status === "completed" || status === "invoiced") return "rechnung";
  return "termine";
}

// Reihenfolge des Verlaufs - daraus ergibt sich, welche Werkzeuge schon
// dran sind. Ein Angebot vor der Besichtigung oder eine Rechnung vor dem
// Abschluss wären Schritte, zu denen der Kundenverlauf nicht passt.
const STATUS_REIHE = [
  "received", "in_review", "inspection_scheduled", "inspection_done",
  "quote_sent", "quote_approved", "scheduled", "in_progress",
  "completed", "invoiced", "paid",
];
const AB_WANN: Record<"termine" | "angebot" | "bericht" | "rechnung", { ab: string; hinweis: string }> = {
  termine: { ab: "received", hinweis: "" },
  angebot: { ab: "inspection_done", hinweis: "freigeschaltet nach der Besichtigung" },
  bericht: { ab: "in_progress", hinweis: "freigeschaltet, sobald die Arbeit läuft" },
  rechnung: { ab: "completed", hinweis: "freigeschaltet nach dem Abschluss" },
};
function istFrei(tabId: "termine" | "angebot" | "bericht" | "rechnung", status?: string): boolean {
  if (!status) return true; // Stand noch nicht geladen - nichts fälschlich sperren
  const jetzt = STATUS_REIHE.indexOf(status);
  const ab = STATUS_REIHE.indexOf(AB_WANN[tabId].ab);
  if (jetzt < 0 || ab < 0) return true;
  return jetzt >= ab;
}

/** Umschalter über den drei Werkzeugen. */
export function KundenWerkzeuge({
  anfrageId, status, onFertig, kundeAnschrift, assignedUserId,
}: { anfrageId: string; status?: string; onFertig: () => void; kundeAnschrift?: string; assignedUserId?: string | null }) {
  // Vorauswahl = nächster sinnvoller Schritt. Die Bürokraft muss nicht
  // überlegen, was jetzt dran ist - das System schlägt es vor.
  const [tab, setTab] = useState<"termine" | "angebot" | "bericht" | "rechnung">(passenderTab(status));
  // Rückt der Vorgang weiter (z.B. Angebot gerade verschickt), springt die
  // Vorauswahl mit auf den nächsten Schritt - und nie auf einen gesperrten.
  useEffect(() => {
    setTab(passenderTab(status));
  }, [status]);
  // Angebot und Rechnung gibt es auf zwei Wegen: hier erstellen oder als
  // fertige PDF hochladen. Der Betrieb wählt selbst.
  const [weg, setWeg] = useState<"erstellen" | "hochladen">("erstellen");
  // Nach einem Storno: Formular mit dem Original vorbefüllen (Neuausstellung).
  const [vorlage, setVorlage] = useState<RechnungsVorlage | null>(null);
  const qc = useQueryClient();
  // Nach jedem Senden auch die eigene Übersicht auffrischen.
  const fertig = () => {
    qc.invalidateQueries({ queryKey: ["termine", anfrageId] });
    qc.invalidateQueries({ queryKey: ["dokumente", anfrageId] });
    onFertig();
  };
  const tabs = [
    { id: "termine" as const, label: "Termine vorschlagen", icon: <CalendarClock className="h-3.5 w-3.5" /> },
    { id: "angebot" as const, label: "Angebot", icon: <FileText className="h-3.5 w-3.5" /> },
    { id: "bericht" as const, label: "Arbeitsbericht", icon: <Check className="h-3.5 w-3.5" /> },
    { id: "rechnung" as const, label: "Rechnung", icon: <FileText className="h-3.5 w-3.5" /> },
  ];
  const mitUpload = tab === "angebot" || tab === "rechnung";

  return (
    <div>
      <KundenUebersicht anfrageId={anfrageId} onStorno={(v) => {
        setVorlage(v); setTab("rechnung"); setWeg("erstellen");
      }} />
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map((t) => {
          const frei = istFrei(t.id, status);
          if (!frei) {
            return (
              <button key={t.id} type="button" disabled
                title={AB_WANN[t.id].hinweis}
                className="btn-secondary btn-sm cursor-not-allowed opacity-45">
                <Lock className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          }
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={tab === t.id ? "btn-primary btn-sm" : "btn-secondary btn-sm"}>
              {t.icon} {t.label}
            </button>
          );
        })}
      </div>
      {tabs.some((t) => !istFrei(t.id, status)) && (
        <p className="mb-3 text-xs text-slate-400">
          {tabs.filter((t) => !istFrei(t.id, status))
            .map((t) => `${t.label}: ${AB_WANN[t.id].hinweis}`)
            .join(" · ")}
        </p>
      )}
      {mitUpload && (
        <div className="mb-3 inline-flex rounded-lg border border-slate-200 p-0.5 text-xs font-medium">
          <button type="button"
            onClick={() => setWeg("erstellen")}
            className={`rounded-md px-3 py-1.5 ${weg === "erstellen" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
            Hier erstellen
          </button>
          <button type="button"
            onClick={() => setWeg("hochladen")}
            className={`rounded-md px-3 py-1.5 ${weg === "hochladen" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
            PDF hochladen
          </button>
        </div>
      )}
      {tab === "termine" && <TermineVorschlagen anfrageId={anfrageId} onFertig={fertig} assignedUserId={assignedUserId} />}
      {tab === "angebot" && (weg === "hochladen"
        ? <PdfHochladen anfrageId={anfrageId} art="angebot" onFertig={fertig} />
        : <DokumentSchreiben anfrageId={anfrageId} art="angebot" onFertig={fertig} />)}
      {tab === "bericht" && <DokumentSchreiben anfrageId={anfrageId} art="arbeitsbericht" onFertig={fertig} />}
      {tab === "rechnung" && (weg === "hochladen"
        ? <PdfHochladen anfrageId={anfrageId} art="rechnung" onFertig={fertig} />
        : <DokumentSchreiben key={vorlage ? "neuausstellung" : "leer"}
            anfrageId={anfrageId} art="rechnung" vorlage={vorlage ?? undefined}
            kundeAnschrift={kundeAnschrift}
            onFertig={() => { setVorlage(null); fertig(); }} />)}
    </div>
  );
}
